import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import ContactBoxes from '../components/ContactBoxes';
import { COMPANY_INFO, SERVICES_LIST } from '../data/mockData';

export default function GalleryPage() {
  const [galleryItems, setGalleryItems] = useState([]);
  const [activeFilter, setActiveFilter] = useState('alle');
  const [selectedImage, setSelectedImage] = useState(null); // { url, title, text }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/v2/content/galleri')
      .then(res => res.json())
      .then(data => {
        if (data && data.items) {
          setGalleryItems(data.items);
        }
      })
      .catch(err => console.error('Error fetching gallery:', err))
      .finally(() => setLoading(false));
  }, []);

  const filters = [
    { label: 'Alle', value: 'alle' },
    ...SERVICES_LIST.map(s => ({ label: s.name, value: s.slug }))
  ];

  const filteredItems = (activeFilter === 'alle'
    ? galleryItems
    : galleryItems.filter(item => item.tilknyttet_ydelse === activeFilter)
  ).filter(item => {
    const img = item.billeder && item.billeder[0];
    return img && !img.includes('307918904-1145084119439492-2392609862970934347-n.jpg');
  });

  return (
    <div className="space-y-16 pb-16">
      <SEO 
        title="Galleri - K-TO Ventilationsteknik"
        description="Se billeder fra vores udførte ventilationsopgaver, teknikrum, indregulering, rensning, fejlfinding og montering på Fyn og i Jylland."
        slug="/galleri"
      />

      {/* Header */}
      <section className="bg-slate-900 text-white py-16 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-sky-500/20 text-sky-300 text-xs font-bold uppercase tracking-wider border border-sky-500/30">
            <i className="fa-solid fa-images text-sky-400" aria-hidden="true"></i>
            <span>Billedgalleri</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white">
            Galleri
          </h1>

          <p className="text-slate-300 text-base sm:text-lg max-w-3xl leading-relaxed">
            Se udvalgte billeder fra vores arbejde i teknikrum, med indregulering, kanalrensning, montering, fejlfinding, køl og varmepumper.
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        
        {/* Category Filters */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
          {filters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setActiveFilter(filter.value)}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-sky-500 ${
                activeFilter === filter.value
                  ? 'bg-sky-600 text-white shadow-md'
                  : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="py-12 text-center text-slate-500">
            <i className="fa-solid fa-spinner animate-spin text-2xl text-sky-600 mb-2"></i>
            <p className="text-xs font-semibold">Henter galleri fra CMS...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="bg-slate-50 rounded-2xl p-8 text-center text-slate-600 border border-slate-200">
            <p className="text-sm font-semibold">Ingen billeder fundet i denne kategori endnu.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredItems.map((item) => {
              const imageUrl = item.billeder && item.billeder[0] ? item.billeder[0] : null;
              if (!imageUrl) return null;

              return (
                <div
                  key={item.id}
                  onClick={() => setSelectedImage({
                    url: imageUrl,
                    title: item.gallerinavn,
                    text: item.billedtekster
                  })}
                  className="group relative bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer flex flex-col"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100 relative">
                    <img 
                      src={imageUrl} 
                      alt={item.gallerinavn || 'Galleri billede'} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center text-white">
                      <span className="w-10 h-10 rounded-full bg-sky-600 flex items-center justify-center text-sm shadow-lg">
                        <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setSelectedImage(null)}
        >
          <div 
            className="relative max-w-4xl w-full bg-slate-900 rounded-3xl overflow-hidden shadow-2xl border border-slate-800 text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-slate-800/80 hover:bg-slate-700 text-white flex items-center justify-center text-lg transition-colors focus:outline-none"
              aria-label="Luk billede"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>

            <div className="max-h-[75vh] bg-black flex items-center justify-center">
              <img 
                src={selectedImage.url} 
                alt={selectedImage.title} 
                className="max-h-[75vh] w-auto max-w-full object-contain"
              />
            </div>

            <div className="p-6 bg-slate-900 border-t border-slate-800 space-y-1">
              <h3 className="text-lg font-bold text-white">{selectedImage.title}</h3>
              {selectedImage.text && (
                <p className="text-xs text-slate-300">{selectedImage.text}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Contact Boxes */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ContactBoxes />
      </section>
    </div>
  );
}
